import React, { useState } from "react";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { num, money } from "../lib/holdingsMath";
import { useBreaches, ALERT_APPS, ALERT_LEVELS } from "../lib/alerts";
import { Plus, Trash2, Bell, BellRing, Send, AlertTriangle, RefreshCw } from "lucide-react";

const APP_LABEL = { holdings: "Holdings", currency: "Currency", benefits: "Benefits", family: "Family Tree" };
const fmt = (app, v) => (app === "family" ? `${v}` : money(v));
const blank = { app: "holdings", level: "total", name: "", direction: "below", limit: "", notify_email: "", notify_phone: "", auto_notify: false, enabled: true };

export default function Alerts() {
  const { results, breaches, refresh } = useBreaches();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [msg, setMsg] = useState("");

  const save = async () => {
    setMsg("");
    try {
      await client.post("/alerts", { data: { ...form, limit: num(form.limit) } });
      setOpen(false); setForm(blank); refresh();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (id) => { await client.delete(`/alerts/${id}`); refresh(); };

  const checkNow = async () => {
    setMsg("Evaluating…");
    try {
      const { data } = await client.post("/alerts/evaluate");
      setMsg(`Evaluated ${data.evaluated} alert(s) — ${data.breached} currently breached. Auto-notifications fire on the server when a new breach occurs.`);
      refresh();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const notify = async (res) => {
    const r = res.rule;
    const title = `Alert: ${APP_LABEL[r.app]} ${r.level} "${r.name || "total"}" ${r.direction} ${r.app === "family" ? r.limit : "$" + r.limit}`;
    const message = `${title}. Current value: ${fmt(r.app, res.value)}.`;
    try {
      const { data } = await client.post("/alerts/notify", { title, message, email: r.notify_email || null, phone: r.notify_phone || null });
      const st = (data.deliveries || []).map((d) => `${d.channel}:${d.status}`).join(", ") || "no channels set";
      setMsg(`Notification sent → ${st}`);
    } catch (e) { setMsg(apiErr(e)); }
  };

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Bell size={24} /> {TL("Alerts")}</h1>
        {breaches.length > 0
          ? <Badge color="danger"><BellRing size={12} className="inline" /> {breaches.length} breached</Badge>
          : <Badge color="success">{TL("all within limits")}</Badge>}
        <div className="ms-auto flex gap-2">
          <Button variant="ghost" onClick={checkNow} data-testid="check-alerts"><RefreshCw size={16} /> {TL("Check now")}</Button>
          <Button onClick={() => setOpen(true)} data-testid="add-alert"><Plus size={16} /> {TL("New alert")}</Button>
        </div>
      </div>
      <p className="text-sm opacity-70 max-w-3xl">{TL("Define limits on any app's items, categories/groups or totals. When a value falls below or exceeds a limit it is flagged here. Turn on")} <b>{TL("auto-notify")}</b> {TL("and the server will email/SMS you automatically the moment a new breach occurs (checked every few minutes), even when the app is closed.")}</p>

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="alert-msg">{msg}</Card>}

      <div className="grid gap-3">
        {results.length === 0 && <Card className="text-center opacity-60">{TL("No alerts defined yet.")}</Card>}
        {results.map((res) => (
          <Card key={res.rule.id} className={`flex flex-wrap items-center gap-3 ${res.breached ? "border-[#DA1E28]/50" : ""}`} data-testid={`alert-${res.rule.id}`}>
            {res.breached ? <AlertTriangle size={18} className="text-[#DA1E28]" /> : <Bell size={18} className="opacity-40" />}
            <div className="font-medium">{APP_LABEL[res.rule.app]}</div>
            <Badge color="info">{res.rule.level}{res.rule.name ? `: ${res.rule.name}` : ""}</Badge>
            {res.rule.auto_notify && <Badge color="info" data-testid={`auto-${res.rule.id}`}>{TL("auto-notify")}</Badge>}
            <span className="text-sm opacity-70">{res.rule.direction === "below" ? "alert if below" : "alert if above"} {fmt(res.rule.app, num(res.rule.limit))}</span>
            <span className="ms-auto text-sm">{TL("Current:")} <b className={res.breached ? "text-[#DA1E28]" : ""}>{fmt(res.rule.app, res.value)}</b></span>
            {res.breached ? <Badge color="danger">{TL("BREACHED")}</Badge> : <Badge color="success">{TL("OK")}</Badge>}
            {res.breached && (res.rule.notify_email || res.rule.notify_phone) && (
              <Button variant="subtle" onClick={() => notify(res)} data-testid={`notify-${res.rule.id}`}><Send size={14} /> {TL("Notify")}</Button>
            )}
            <button onClick={() => del(res.rule.id)} data-testid={`del-alert-${res.rule.id}`} className="opacity-50 hover:opacity-100 hover:text-[#DA1E28]"><Trash2 size={15} /></button>
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={TL("New alert rule")} testid="alert-modal">
        <div className="grid grid-cols-2 gap-3">
          <Select label={TL("Application")} value={form.app} onChange={(e) => setForm({ ...form, app: e.target.value })} data-testid="alert-app">
            {ALERT_APPS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
          </Select>
          <Select label={TL("Scope")} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="alert-level">
            {ALERT_LEVELS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
          </Select>
          {form.level !== "total" && (
            <Input label={TL("Item / category name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="alert-name" />
          )}
          <Select label={TL("Trigger when")} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="alert-direction">
            <option value="below">{TL("Falls below limit")}</option>
            <option value="above">{TL("Exceeds limit")}</option>
          </Select>
          <Input label={TL("Limit")} type="number" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} data-testid="alert-limit" />
          <Input label={TL("Notify email (optional)")} type="email" value={form.notify_email} onChange={(e) => setForm({ ...form, notify_email: e.target.value })} data-testid="alert-email" />
          <Input label={TL("Notify SMS phone (optional)")} value={form.notify_phone} onChange={(e) => setForm({ ...form, notify_phone: e.target.value })} data-testid="alert-phone" />
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.auto_notify} onChange={(e) => setForm({ ...form, auto_notify: e.target.checked })} data-testid="alert-auto" className="h-4 w-4" />
            Auto-notify by email/SMS when newly breached (server checks periodically; requires an email or phone above)
          </label>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>{TL("Cancel")}</Button>
            <Button onClick={save} data-testid="save-alert">{TL("Save alert")}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
